<script>
  import Vue from 'vue';
  import axios from 'axios';
  import TweenLite from 'gsap/umd/TweenLite';
  import { Quad, Linear } from 'gsap/umd/EasePack';

  import Icon from '@/scripts/old/modules/Icon.vue';
  import { declOfNums, deepClone } from '@/scripts/old/helpers/utils';

  const DIAL = {
    two1: {
      angle: 0,
      points: 2,
    },
    three1: {
      angle: 26,
      points: 3,
    },
    miss1: {
      angle: 58,
      points: 0,
    },
    one1: {
      angle: 90,
      points: 1,
    },
    three2: {
      angle: 118,
      points: 3,
    },
    bar1: {
      angle: 150,
      points: 0,
    },
    two2: {
      angle: 183,
      points: 2,
    },

    miss2: {
      angle: 215,
      points: 0,
    },

    one2: {
      angle: 248,
      points: 1,
    },

    three3: {
      angle: 278,
      points: 3,
    },

    bar2: {
      angle: 324,
      points: 0,
    },
  };

  const SERVER_ERROR = 'К сожалению, произошла ошибка. Попробуйте позже еще раз.';

  const LOCALES = {
    ru: {
      title: '<span class="text-color-brand">«Мой малыш – чемпион!»</span><br>Ты сам решаешь, <span class="text-color-sub">кто победит!</span>',
      scoreGoalTitle: '<span class="text-color-brand">Забей гол</span> в&nbsp;ворота соперника!',
      resultTitleMiddle(result) {
        return `Твой результат – <span class="text-color-brand">${result}&nbsp;${declOfNums(result, ['гол', 'гола', 'голов'])}.</span> <span class="text-color-sub">Сыграй еще!</span>`;
      },
      resultTitleEnd(result) {
        return `Твой результат – <span class="text-color-brand">${result}&nbsp;${declOfNums(result, ['гол', 'гола', 'голов'])}.</span>`;
      },
      resultTitleEndGood(result) {
        return `<span class="text-color-sub">Отлично сыграно!</span> Твой результат – <span class="text-color-brand">${result}&nbsp;${declOfNums(result, ['гол', 'гола', 'голов'])}.</span>`;
      },
      selectTeam: 'Выбери команду, за которую будешь играть',
      hit: 'Бей!',
      playAgain: 'Сыграть ещё',
      attemptsStatus({ done, total }) {
        return `${declOfNums(done, ['Использована', 'Использовано', 'Использовано'])} <span class="text-color-sub">${done}</span> ${declOfNums(done, ['попытка', 'попытки', 'попыток'])} из <span class="text-color-brand">${total}</span>`;
      },
      gameAdvice: 'У вас 2 попытки забить гол, результат игры - сумма голов по итогам 2х попыток.',
      endGameText: '<span class="text-color-brand">Ключ к&nbsp;победе - успешный пас</span> <br><span class="text-color-sub">Отдай пас</span> своим друзьям - увеличь шансы на&nbsp;победу, пригласив в игру',
      endGameTextGood: '<span class="text-color-brand">Увеличь шансы на&nbsp;победу</span> <br><span class="text-color-sub">Отдай пас</span> своему другу – пригласи его в&nbsp;игру',
      saveResult: 'Сохранить результат',
      givePass: 'Отдать пас',
      goToRatings: 'Перейти к рейтингам',
    },
    en: {
      title: '<span class="text-color-brand">«My baby is the champion!»</span><br>You decide <span class="text-color-sub">who wins!</span>',
      scoreGoalTitle: '<span class="text-color-brand">Score a goal</span> against the opponent!',
      resultTitleMiddle(result) {
        return `Your result is <span class="text-color-brand">${result}&nbsp;${declOfNums(result, ['goal', 'goals', 'goals'])}.</span> <span class="text-color-sub">Try again!</span>`;
      },
      resultTitleEnd(result) {
        return `Your result is <span class="text-color-brand">${result}&nbsp;${declOfNums(result, ['goal', 'goals', 'goals'])}.</span>`;
      },
      resultTitleEndGood(result) {
        return `<span class="text-color-sub">Good game!</span> Your result is <span class="text-color-brand">${result}&nbsp;${declOfNums(result, ['goal', 'goals', 'goals'])}.</span>`;
      },
      selectTeam: 'Choose the team you will play for',
      hit: 'Hit me!',
      playAgain: 'Try again',
      attemptsStatus({ done, total }) {
        return `Used <span class="text-color-sub">${done}</span> out of <span class="text-color-brand">${total}</span> attempts`;
      },
      gameAdvice: 'You have 2 attempts to score a goal, the result of the game is the sum of the goals on the basis of 2 attempts.',
      endGameText: '<span class="text-color-brand">The key to winning is a successful pass.</span> <br><span class="text-color-sub">Give a pass</span> to your friends to increase your chance to win by inviting them to the game',
      endGameTextGood: '<span class="text-color-brand">Increase your chances to win</span> <br><span class="text-color-sub">Give a pass</span> to your friend and invite him into the game',
      saveResult: 'Save the results',
      givePass: 'Give a pass',
      goToRatings: 'To the ratings',
    },
  };

  export default {
    components: {
      Icon,
    },
    props: {
      team: {
        type: Object,
        default: null,
        // id
        // flag
        // color
      },
      teams: {
        type: Array,
        default: () => ([]),
      },
      attempts: {
        type: Number,
        default: 2,
      },
      attemptsDone: {
        type: Number,
        default: 0,
      },
      lastResult: {
        type: String,
        default: '',
      },
      points: {
        type: Number,
        default: 0,
      },
      saved: {
        type: Boolean,
        default: false,
      },
      endpoint: {
        type: String,
        default: '#',
      },
      ratingsUrl: {
        type: String,
        default: '#',
      },
      saveUrl: {
        type: String,
        default: '#',
      },
      lang: {
        type: String,
        default: 'ru',
      },
    },
    data() {
      return {
        mutableTeam: null,
        mutableAttemptsDone: 0,
        mutableResult: '',
        mutablePoints: 0,
        rotateAngle: 0,
        isWaiting: true,
        isPlaying: false,
        isLoading: false,
        imagesPath: `${window.AppConfig.staticPath}images/`,
        error: '',
      };
    },
    computed: {
      goodResult() {
        return this.mutablePoints > 0;
      },
      wrapClass() {
        if (this.isPlaying) return '_playing';
        if (this.isWaiting) return '_waiting';
        return '';
      },
    },
    beforeMount() {
      this.mutableTeam = this.team;
      this.mutableAttemptsDone = this.attemptsDone;
      this.mutableResult = this.lastResult;
      this.mutablePoints = this.points;

      this.isWaiting = !this.mutableResult
        && this.mutableAttemptsDone < this.attempts
        && !this.saved;

      if (this.lastResult && DIAL[this.lastResult]) {
        this.rotateAngle = DIAL[this.lastResult].angle;
      }
    },
    methods: {
      selectTeam(team) {
        if (this.mutableTeam || this.isLoading) return;
        this.isLoading = true;
        this.error = '';

        axios({
          method: 'post',
          url: this.endpoint,
          data: {
            team,
            action: 'selectTeam',
          },
        })
          .then(({ data }) => {
            this.isLoading = false;

            if (data.error) {
              this.error = data.error;
              return;
            }

            if (!data.result) {
              this.error = SERVER_ERROR;
              return;
            }

            this.mutableTeam = data.result;
            this.$emit('selectTeam', { team: deepClone(this.mutableTeam) });
          })
          .catch(() => {
            this.isLoading = false;
            this.error = SERVER_ERROR;
          });
      },
      play() {
        if (this.isPlaying || !this.isWaiting || this.isLoading) return;
        if (this.mutableAttemptsDone >= this.attempts) return;

        this.error = '';
        this.isWaiting = false;
        this.isPlaying = true;

        this.rotateAngle %= 360;

        const start = this.rotateAngle;
        let winnerAngle;

        const roll = (target, last) => {
          const rotate = { rotate: this.rotateAngle };

          let time = ((target - this.rotateAngle) / 270);
          if (winnerAngle) {
            time *= 2; // Speed fix for easing
          }

          TweenLite.to(rotate, time, {
            rotate: target,
            ease: winnerAngle ? Quad.easeOut : Linear.easeNone,
            onUpdate: () => {
              this.rotateAngle = rotate.rotate;
            },
            onComplete: () => {
              if (last) {
                this.isPlaying = false;
                if (!this.mutableResult) {
                  this.isWaiting = true;
                }
                return;
              }

              if (winnerAngle >= 0) {
                this.rotateAngle = 0;
                Vue.nextTick(() => {
                  roll(winnerAngle + 360, true);
                });

                return;
              }

              roll(360, false);
            },
          });
        };

        roll(start + (360 - start) + 360, false);

        axios({
          method: 'post',
          url: this.endpoint,
          data: {
            action: 'play',
          },
        })
          .then(({ data }) => {
            if (data.error) {
              this.error = data.error;
              winnerAngle = start;
              return;
            }

            if (!data.result || !DIAL[data.result]) {
              this.error = SERVER_ERROR;
              winnerAngle = start;
              return;
            }

            this.mutableAttemptsDone++;
            this.mutableResult = data.result;
            this.mutablePoints += DIAL[data.result].points;
            winnerAngle = DIAL[data.result].angle;
            this.$emit('play', { attempt: this.mutableAttemptsDone });
          })
          .catch(() => {
            this.error = SERVER_ERROR;
            winnerAngle = start;
          });
      },
      playAgain() {
        if (this.mutableAttemptsDone >= this.attempts) return;
        this.mutableResult = '';
        this.isWaiting = true;
        this.$emit('playAgain', { attempt: this.mutableAttemptsDone });
      },
      declOfNums(number, titles) {
        return declOfNums(number, titles);
      },
      getTranslate(key, params) {
        const locale = LOCALES[this.lang];
        if (!locale) {
          return key;
        }

        if (typeof locale[key] === 'string') {
          return locale[key];
        }

        if (typeof locale[key] === 'function') {
          return locale[key].call(this, params);
        }

        return key;
      },
    },
  };
</script>

<template>
  <div
    :lang="lang"
    :class="wrapClass"
    class="fifa2018-game"
  >
    <div
      v-if="isLoading"
      class="fifa2018-game__loading"
    >
      <div class="loading" />
    </div>
    <template v-else-if="!mutableTeam">
      <div class="container">
        <div
          class="fifa2018-game__title"
          v-html="getTranslate('title')"
        />
      </div>
      <div class="fifa2018-game__teams">
        <div class="container">
          <div class="fifa2018-game__teams-content">
            <div class="fifa2018-game__teams-title">
              {{ getTranslate('selectTeam') }}
            </div>
            <div class="fifa2018-game__teams-select">
              <div
                v-for="(teamsItem) in teams"
                :key="teamsItem.id"
                class="fifa2018-game__team"
              >
                <div class="fifa2018-game__team-flag">
                  <img
                    :src="teamsItem.flag"
                    width="92"
                    height="60"
                    alt=""
                  >
                </div>
                <button
                  :class="{[`fifa2018-game__team-ball_${teamsItem.color}`]: teamsItem.color}"
                  type="button"
                  class="fifa2018-game__team-ball"
                  @click="selectTeam(teamsItem)"
                />
                <div class="fifa2018-game__team-ball-shadow" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="error"
        class="container"
      >
        <div class="fifa2018-game__error">
          {{ error }}
        </div>
      </div>
    </template>
    <template v-else>
      <div class="container">
        <div class="fifa2018-game__title">
          <div
            v-if="mutableResult && mutableAttemptsDone < attempts && !isPlaying"
            v-html="getTranslate('resultTitleMiddle', mutablePoints)"
          />
          <template v-else-if="mutableResult && !isPlaying">
            <div
              v-if="goodResult"
              v-html="getTranslate('resultTitleEndGood', mutablePoints)"
            />
            <div
              v-else
              v-html="getTranslate('resultTitleEnd', mutablePoints)"
            />
          </template>
          <template v-else>
            <div v-html="getTranslate('scoreGoalTitle')" />
          </template>
        </div>
        <div class="fifa2018-game__field">
          <div class="fifa2018-game__dial" />
          <div
            v-if="mutableResult && !isPlaying"
            class="fifa2018-game__result-dial-wrap"
          >
            <div
              :style="{ transform: `rotate(${rotateAngle}deg)`}"
              class="fifa2018-game__result-dial"
            >
              <div class="fifa2018-game__result-dial-sector">
                <div
                  :style="{ transform: `rotate(${-rotateAngle}deg)`}"
                  class="fifa2018-game__result-dial-image"
                />
              </div>
            </div>
          </div>
          <button
            :class="{[`fifa2018-game__ball_${mutableTeam.color}`]: mutableTeam.color}"
            class="fifa2018-game__ball"
            type="button"
            @click="play"
          />
          <div
            v-if="isWaiting"
            class="fifa2018-game__bubble"
          >
            {{ getTranslate('hit') }}
          </div>
          <div class="fifa2018-game__arrow-wrap">
            <div
              :style="{ transform: `rotate(${rotateAngle}deg)`}"
              class="fifa2018-game__arrow"
            />
          </div>
        </div>
        <div
          v-if="error"
          class="fifa2018-game__error"
        >
          {{ error }}
        </div>
        <div
          v-if="!saved && !isPlaying"
          class="fifa2018-game__status"
        >
          <template v-if="mutableAttemptsDone < attempts">
            <div
              v-html="getTranslate('attemptsStatus', {
                done: mutableAttemptsDone,
                total: attempts
              })"
            />
            <div class="text-base">
              {{ getTranslate('gameAdvice') }}
            </div>
          </template>
          <template v-else>
            <div
              v-if="mutablePoints > 0"
              v-html="getTranslate('endGameTextGood')"
            />
            <div
              v-else
              v-html="getTranslate('endGameText')"
            />
          </template>
        </div>
        <div
          v-if="!isPlaying"
          class="fifa2018-game__сtrls"
        >
          <div
            v-if="!saved"
            class="grid grid_center"
          >
            <div
              v-if="mutableResult && mutableAttemptsDone < attempts"
              class="col col_3"
            >
              <button
                type="button"
                class="btn btn_fluid"
                @click="playAgain"
              >
                <icon
                  class="btn__icon"
                  src="repeat"
                />
                &nbsp;
                <span>{{ getTranslate('playAgain') }}</span>
              </button>
            </div>
            <div
              v-if="mutableAttemptsDone >= attempts"
              class="col col_3"
            >
              <a
                :href="saveUrl"
                class="btn btn_fluid btn_brand"
                @click="$emit('pass')"
              >
                <span v-if="mutablePoints > 0">{{ getTranslate('saveResult') }}</span>
                <span v-else>{{ getTranslate('givePass') }}</span>
              </a>
            </div>
          </div>
          <div
            v-if="saved"
            class="text-center"
          >
            <a
              :href="ratingsUrl"
              class="text-big link link_alt link_dashed"
            >
              {{ getTranslate('goToRatings') }}
            </a>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
